/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase, updateSupabaseConfig } from './lib/supabase';
import { AnimatedTestimonials } from './components/ui/animated-testimonials';
import { 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight, 
  Menu, 
  X, 
  Lock, 
  User, 
  Phone, 
  HelpCircle, 
  Info,
  LogOut,
  ChevronRight,
  Zap,
  Copy,
  Check,
  Bell,
  MessageSquare,
  Settings,
  Trash2,
  Megaphone,
  Activity,
  Database,
  Server
} from 'lucide-react';

declare global {
  interface Window {
    particlesJS: any;
  }
}

// --- Types ---

type Page = 'home' | 'login' | 'register' | 'dashboard' | 'about' | 'privacy' | 'faq' | 'admin_login' | 'admin_dashboard';

interface UserData {
  id: string;
  name: string;
  email: string;
  isAdmin?: boolean;
}

interface UnlockRequest {
  id: string;
  user_id: string;
  name: string;
  email: string;
  contact_phone: string;
  unlock_number: string;
  status: string;
  payment_status?: string;
  admin_notes?: string;
  user_notice?: string;
  user_notice_read?: boolean;
  created_at: string;
}

// --- Components ---

const Navbar = ({ currentPage, setPage, user, logout }: { 
  currentPage: Page, 
  setPage: (p: Page) => void, 
  user: UserData | null,
  logout: () => void
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [clickCount, setClickCount] = useState(0);

  const handleLogoClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    if (newCount === 5) {
      setPage('admin_login');
      setClickCount(0);
    }
    // Reset count after 2 seconds of inactivity
    setTimeout(() => setClickCount(0), 2000);
  };

  const navItems = [
    { label: 'Início', page: 'home' as Page },
    { label: 'Sobre', page: 'about' as Page },
    { label: 'FAQ', page: 'faq' as Page },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#075E54]/95 backdrop-blur-sm border-b border-white/10 h-16 lg:h-20 flex items-center">
      <div className="max-w-[1600px] w-full mx-auto px-4 lg:px-8 flex justify-between items-center">
        <div className="flex items-center cursor-pointer select-none" onClick={handleLogoClick}>
          <img 
            src="https://vyemarhrtuzplljpfpbs.supabase.co/storage/v1/object/public/Imagens/whatsapp_proibido.ico" 
            alt="Logo" 
            className="h-8 w-8 lg:h-10 lg:w-10 mr-2 lg:mr-3"
            referrerPolicy="no-referrer"
          />
          <span className="text-xl lg:text-3xl font-bold tracking-tight text-white whitespace-nowrap">
            UnBlocker <span className="text-[#25D366]">WA 24hs</span>
          </span>
        </div>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center bg-white/10 rounded-full px-2 py-1 border border-white/20">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => setPage(item.page)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                currentPage === item.page ? 'bg-[#25D366] text-white' : 'text-white hover:bg-white/10'
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="hidden lg:flex items-center space-x-6">
          {user ? (
            <div className="flex items-center space-x-4">
              <div className="flex items-center bg-white/10 rounded-full px-4 py-2 border border-white/20">
                <User className="h-4 w-4 text-[#25D366] mr-2" />
                <span className="text-white text-sm font-medium">{user.name}</span>
                {user.isAdmin && (
                  <button 
                    onClick={() => setPage('admin_dashboard')}
                    className="ml-3 text-[10px] bg-emerald-500 text-white px-2 py-0.5 rounded-full font-bold hover:bg-emerald-400 transition-colors"
                  >
                    ADM
                  </button>
                )}
              </div>
              <button 
                onClick={logout}
                className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all border border-white/10"
                title="Sair"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <>
              <button 
                onClick={() => setPage('login')}
                className="text-white hover:text-[#25D366] transition-colors font-medium"
              >
                Entrar
              </button>
              <button 
                onClick={() => setPage('login')}
                className="bg-[#25D366] text-white px-8 py-3 rounded-full text-sm font-bold hover:bg-[#128C7E] transition-all shadow-lg shadow-[#25D366]/20"
              >
                Solicitar desbloqueio
              </button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center">
          <button onClick={() => setIsOpen(!isOpen)} className="text-white p-2">
            {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden absolute top-16 left-0 right-0 bg-[#075E54] border-b border-white/10 overflow-hidden shadow-2xl"
          >
            <div className="flex flex-col p-6 space-y-4">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setPage(item.page);
                    setIsOpen(false);
                  }}
                  className={`text-left py-3 px-4 rounded-xl text-lg font-medium transition-all ${
                    currentPage === item.page ? 'bg-[#25D366] text-white' : 'text-white hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <div className="pt-4 border-t border-white/10">
                {user ? (
                  <div className="space-y-4">
                    <div className="flex items-center bg-white/10 rounded-xl px-4 py-3 border border-white/20">
                      <User className="h-5 w-5 text-[#25D366] mr-3" />
                      <span className="text-white font-medium">{user.name}</span>
                      {user.isAdmin && (
                        <button 
                          onClick={() => {
                            setPage('admin_dashboard');
                            setIsOpen(false);
                          }}
                          className="ml-auto bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold"
                        >
                          ADM
                        </button>
                      )}
                    </div>
                    <button 
                      onClick={() => {
                        logout();
                        setIsOpen(false);
                      }}
                      className="w-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white py-4 rounded-xl transition-all border border-white/10 font-bold"
                    >
                      <LogOut className="h-5 w-5 mr-2" /> Sair
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col space-y-3">
                    <button 
                      onClick={() => {
                        setPage('login');
                        setIsOpen(false);
                      }}
                      className="w-full py-4 text-white font-bold text-center border border-white/20 rounded-xl"
                    >
                      Entrar
                    </button>
                    <button 
                      onClick={() => {
                        setPage('login');
                        setIsOpen(false);
                      }}
                      className="w-full bg-[#25D366] text-white py-4 rounded-xl font-bold shadow-lg shadow-[#25D366]/20"
                    >
                      Solicitar desbloqueio
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const Footer = ({ setPage }: { setPage: (p: Page) => void }) => (
  <footer className="bg-[#075E54] text-white/70 py-12 border-t border-white/10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="col-span-1 md:col-span-2">
          <div className="flex items-center text-white mb-4">
            <img 
              src="https://vyemarhrtuzplljpfpbs.supabase.co/storage/v1/object/public/Imagens/whatsapp_proibido.ico" 
              alt="Logo" 
              className="h-8 w-8 mr-2"
              referrerPolicy="no-referrer"
            />
            <span className="ml-2 text-lg font-bold">UnBlocker <span className="text-[#25D366]">WA 24hs</span></span>
          </div>
          <p className="text-sm max-w-xs">
            Especialistas em recuperação de contas do WhatsApp. Rápido, seguro e totalmente sigiloso.
          </p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Links Úteis</h4>
          <ul className="space-y-2 text-sm">
            <li><button onClick={() => setPage('about')} className="hover:text-[#25D366] transition-colors">Sobre Nós</button></li>
            <li><button onClick={() => setPage('faq')} className="hover:text-[#25D366] transition-colors">Ajuda / FAQ</button></li>
            <li><button onClick={() => setPage('privacy')} className="hover:text-[#25D366] transition-colors">Privacidade</button></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Suporte</h4>
          <p className="text-sm">Disponível 24/7 para solicitações urgentes.</p>
          <p className="text-sm mt-2 text-[#25D366]">suporte@unblockerwa.com</p>
        </div>
      </div>
      <div className="border-t border-white/10 mt-12 pt-8 text-center text-xs">
        <p>&copy; 2025 UnBlocker WA 24hs. Todos os direitos reservados.</p>
      </div>
    </div>
  </footer>
);

const ParticlesBackground = () => {
  useEffect(() => {
    if (window.particlesJS) {
      window.particlesJS("particles-js", {
        particles: {
          number: { value: 70 },
          color: { value: "#25D366" },
          shape: { type: "circle" },
          opacity: { value: 0.3 },
          size: { value: 3 },
          move: { enable: true, speed: 2 },
          line_linked: {
            enable: true,
            distance: 150,
            color: "#25D366",
            opacity: 0.2,
            width: 1
          }
        },
        interactivity: {
          detect_on: "canvas",
          events: {
            onhover: { enable: true, mode: "grab" },
            onclick: { enable: true, mode: "push" }
          }
        },
        retina_detect: true
      });
    }
  }, []);

  return <div id="particles-js" className="absolute inset-0 z-0 pointer-events-none" />;
};

// --- Pages ---

const TestimonialsSection = () => {
  const testimonials = [
    {
      quote: "Confesso que achei que poderia ser golpe, mas arrisquei. Fui surpreendida! Serviço rápido, avisaram quando liberou e no mesmo dia voltei a vender. Excelente!",
      name: "Ana Lúcia",
      designation: "Gás Maias",
      src: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=3387&auto=format&fit=crop",
    },
    {
      quote: "Meu WhatsApp voltou antes do prazo e me avisaram na hora. Atendimento sério e eficiente. Serviço confiável, recomendo!",
      name: "Pedro Neves",
      designation: "VipNet",
      src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=3387&auto=format&fit=crop",
    },
    {
      quote: "Rápidos e muito profissionais. No mesmo dia já estava negociando com clientes novamente. Resolveram tudo com excelência!",
      name: "Fernando Sobral",
      designation: "Pizzaria Bella Massa",
      src: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=3387&auto=format&fit=crop",
    },
    {
      quote: "Fiquei com medo no início, mas o serviço é real e muito eficaz. Me notificaram quando liberou e voltou tudo rápido. Amei!",
      name: "Vanessa Gimenez",
      designation: "Estética Vanessa",
      src: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?q=80&w=3388&auto=format&fit=crop",
    },
    {
      quote: "Equipe muito eficiente! Resolveram dentro do prazo e com comunicação clara. Meu WhatsApp voltou rápido. Recomendo muito!",
      name: "Lúcia Monteiro",
      designation: "Cell Prime",
      src: "https://images.unsplash.com/photo-1554151228-14d9def656e4?q=80&w=3386&auto=format&fit=crop",
    },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">O que nossos clientes dizem</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Milhares de pessoas já recuperaram suas contas com o UnBlocker WA 24hs. Veja alguns depoimentos reais.
          </p>
        </div>
        <AnimatedTestimonials testimonials={testimonials} autoplay={true} />
      </div>
    </section>
  );
};

const MarqueeBanner = () => {
  return (
    <div className="bg-[#075E54] py-4 overflow-hidden whitespace-nowrap relative z-30 border-y border-[#25D366]/30 shadow-2xl">
      <motion.div
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        className="flex items-center gap-16 w-max"
      >
        {[...Array(20)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 lg:gap-6">
            <span className="text-white font-black text-xl lg:text-3xl tracking-tighter drop-shadow-md">
              APENAS <span className="text-[#25D366]">20 REAIS</span>
            </span>
            <motion.div
              animate={{ 
                scale: [1, 1.3, 1],
                rotate: [0, -15, 15, 0],
              }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="text-2xl lg:text-4xl filter drop-shadow-[0_0_8px_rgba(37,211,102,0.5)]"
            >
              🔓
            </motion.div>
          </div>
        ))}
      </motion.div>
    </div>
  );
};

const HomePage = ({ setPage }: { setPage: (p: Page) => void }) => (
  <div className="relative min-h-screen bg-white">
    <div>
      <MarqueeBanner />
      <ParticlesBackground />
      
      <section className="relative z-10 flex flex-col lg:flex-row items-center justify-between px-4 lg:px-16 py-20 lg:py-0 min-h-screen gap-12">
        <div className="max-w-[700px] text-center lg:text-left relative mt-16 lg:mt-0">
          {/* Background Ring */}
          <div className="absolute -top-10 lg:-top-20 -right-10 lg:-right-20 w-32 lg:w-64 h-32 lg:h-64 opacity-20 pointer-events-none">
             <div className="w-full h-full border-[10px] lg:border-[20px] border-[#25D366] rounded-full" />
             <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 border-[8px] lg:border-[15px] border-[#128C7E] rounded-full rotate-45" />
          </div>

          <motion.h1 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0 }}
            className="text-xl lg:text-[32px] text-[#075E54] font-bold mb-4"
          >
            Recuperação Especializada
          </motion.h1>
          
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="flex flex-col"
          >
            <span className="text-5xl md:text-7xl lg:text-[140px] text-[#075E54] font-bold leading-[0.9] m-0 tracking-tighter">
              WhatsApp
            </span>
            <span className="text-5xl md:text-7xl lg:text-[140px] text-[#075E54] font-bold leading-[0.9] m-0 tracking-tighter">
              Desbloqueado
            </span>
          </motion.div>

          <motion.p 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
            className="mt-6 lg:mt-10 text-base lg:text-[20px] text-[#4a5a7a] leading-relaxed max-w-[500px] mx-auto lg:mx-0"
          >
            Recupere o acesso à sua conta do WhatsApp em tempo recorde. Processo seguro, rápido e totalmente sigiloso para você não perder seus contatos e vendas.
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-8 lg:mt-12 flex flex-col sm:flex-row justify-center lg:justify-start gap-4 lg:gap-6"
          >
            <button 
              onClick={() => setPage('login')}
              className="px-8 lg:px-10 py-4 rounded-full bg-[#25D366] text-white font-bold text-base lg:text-lg transition-all hover:-translate-y-[3px] hover:shadow-xl hover:shadow-[#25D366]/30 cursor-pointer"
            >
              Solicitar desbloqueio
            </button>
            <button 
              onClick={() => setPage('faq')}
              className="px-8 lg:px-10 py-4 rounded-full border-2 border-[#075E54] bg-transparent text-[#075E54] font-bold text-base lg:text-lg transition-all hover:-translate-y-[3px] hover:bg-[#075E54] hover:text-white cursor-pointer"
            >
              Como Funciona
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 100, scale: 0.8 }}
          animate={{ 
            opacity: 1, 
            x: 0,
            scale: 1,
            y: [0, -20, 0],
          }}
          transition={{ 
            opacity: { duration: 1, delay: 1.2 },
            x: { duration: 1, delay: 1.2 },
            scale: { duration: 1, delay: 1.2 },
            y: { duration: 4, repeat: Infinity, ease: "easeInOut" }
          }}
          className="relative w-full max-w-[300px] lg:max-w-[500px]"
        >
          <img 
            src="https://vyemarhrtuzplljpfpbs.supabase.co/storage/v1/object/public/Imagens/verde.png" 
            alt="Hero" 
            className="w-full h-auto object-contain max-h-[110vh] scale-110"
            referrerPolicy="no-referrer"
          />
        </motion.div>
      </section>

      <div className="bg-white">
        {/* Problem Section */}
        <section className="py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-slate-900 mb-6">Conta bloqueada? Você não está sozinho.</h2>
            <p className="text-slate-600 mb-6 text-lg">
              Milhares de usuários enfrentam bloqueios inesperados no WhatsApp, perdendo contatos, vendas e comunicação importante. O UnBlocker WA 24hs foi criado para resolver isso rapidamente.
            </p>
            <ul className="space-y-4">
              {[
                'Perda de contatos comerciais',
                'Interrupção de vendas e suporte',
                'Isolamento de amigos e familiares',
                'Frustração com o suporte oficial lento'
              ].map((item, i) => (
                <li key={i} className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-3 mt-0.5" />
                  <span className="text-slate-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-slate-50 p-8 rounded-3xl border border-slate-100 shadow-inner">
             <div className="space-y-4">
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
                  <div className="h-10 w-10 rounded-full bg-slate-200 mr-4"></div>
                  <div className="flex-1">
                    <div className="h-3 w-24 bg-slate-200 rounded mb-2"></div>
                    <div className="h-2 w-full bg-slate-100 rounded"></div>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center opacity-50">
                  <div className="h-10 w-10 rounded-full bg-slate-200 mr-4"></div>
                  <div className="flex-1">
                    <div className="h-3 w-32 bg-slate-200 rounded mb-2"></div>
                    <div className="h-2 w-3/4 bg-slate-100 rounded"></div>
                  </div>
                </div>
                <div className="bg-red-50 p-6 rounded-2xl border border-red-100 text-center">
                  <Lock className="h-8 w-8 text-red-500 mx-auto mb-3" />
                  <p className="text-red-800 font-bold">Esta conta está impedida de usar o WhatsApp</p>
                  <p className="text-red-600 text-sm mt-1">As conversas ainda estão neste aparelho.</p>
                </div>
             </div>
          </div>
        </div>
      </div>
    </section>

    {/* Solution Section */}
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">A solução definitiva para desbloquear seu WhatsApp</h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Com nosso sistema, você recupera o acesso ao seu número rapidamente, podendo voltar a falar com clientes, amigos e familiares sem dor de cabeça.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Zap, title: 'Algoritmos avançados', desc: 'Processamento inteligente para agilizar a revisão da sua conta.' },
            { icon: CheckCircle2, title: 'Processo seguro', desc: 'Protocolos de segurança rigorosos para proteger sua identidade.' },
            { icon: Lock, title: 'Nenhum dado armazenado', desc: 'Privacidade total. Seus dados são usados apenas para o desbloqueio.' },
            { icon: Info, title: 'Interface simples', desc: 'Passo a passo intuitivo para qualquer pessoa realizar a solicitação.' },
          ].map((item, i) => (
            <div key={i} className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
              <item.icon className="h-10 w-10 text-emerald-600 mb-6" />
              <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* How it Works */}
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-slate-900">Como Funciona</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 relative">
          {/* Connector Line */}
          <div className="hidden md:block absolute top-1/2 left-0 w-full h-0.5 bg-slate-100 -translate-y-1/2 z-0"></div>
          
          {[
            { step: '1', title: 'Cadastre sua solicitação', desc: 'Preencha os dados básicos e o número bloqueado.' },
            { step: '2', title: 'Confirmamos o pedido', desc: 'Nossa equipe valida as informações em minutos.' },
            { step: '3', title: 'Iniciamos o processo', desc: 'Nossos sistemas entram em ação automaticamente.' },
            { step: '4', title: 'Número desbloqueado', desc: 'Seu WhatsApp volta a funcionar normalmente.' },
          ].map((item, i) => (
            <div key={i} className="relative z-10 text-center">
              <div className="h-12 w-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-xl mx-auto mb-6 shadow-lg shadow-emerald-200">
                {item.step}
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-500 text-sm px-4">{item.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-16 text-center">
          <p className="text-emerald-600 font-bold bg-emerald-50 inline-block px-6 py-2 rounded-full">
            Tempo médio: até 6 horas após confirmação
          </p>
        </div>
      </div>
    </section>

    {/* Stats / Social Proof */}
    <section className="py-20 bg-emerald-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          <div>
            <div className="text-5xl font-extrabold mb-2">+3.000</div>
            <div className="text-emerald-100">Desbloqueios realizados</div>
          </div>
          <div>
            <div className="text-5xl font-extrabold mb-2">92%</div>
            <div className="text-emerald-100">Taxa de sucesso</div>
          </div>
          <div>
            <div className="text-5xl font-extrabold mb-2">6h</div>
            <div className="text-emerald-100">Tempo médio de resposta</div>
          </div>
        </div>
      </div>
    </section>

    {/* Testimonials Section */}
    <TestimonialsSection />

    {/* CTA Final */}
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Não espere 48h. Volte a usar seu WhatsApp hoje em até 6hs.</h2>
        <button 
          onClick={() => setPage('register')}
          className="bg-emerald-600 text-white px-10 py-5 rounded-2xl font-bold text-xl hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-200"
        >
          Solicitar Desbloqueio Agora
        </button>
      </div>
    </section>
      </div>
    </div>
  </div>
);

const AuthPage = ({ type, setPage, onAuth }: { type: 'login' | 'register', setPage: (p: Page) => void, onAuth: (u: UserData) => void }) => {
  const [formData, setFormData] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError('O sistema de autenticação não está disponível. Verifique as variáveis de ambiente.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      if (type === 'register') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            data: {
              full_name: formData.name,
            },
          },
        });

        if (signUpError) throw signUpError;
        if (data.user) {
          onAuth({
            id: data.user.id as any,
            name: data.user.user_metadata.full_name || formData.name,
            email: data.user.email || formData.email,
          });
        }
      } else {
        const { data, error: signInError } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (signInError) throw signInError;
        if (data.user) {
          onAuth({
            id: data.user.id as any,
            name: data.user.user_metadata.full_name || 'Usuário',
            email: data.user.email || formData.email,
          });
        }
      }
    } catch (err: any) {
      setError(err.message || 'Ocorreu um erro na autenticação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 flex items-center justify-center px-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-xl border border-slate-100 p-8"
      >
        <div className="text-center mb-8">
          <img 
            src="https://vyemarhrtuzplljpfpbs.supabase.co/storage/v1/object/public/Imagens/whatsapp_proibido.ico" 
            alt="Logo" 
            className="h-12 w-12 mx-auto mb-4"
            referrerPolicy="no-referrer"
          />
          <h2 className="text-2xl font-bold text-slate-900">
            {type === 'register' ? 'Criar sua conta' : 'Acessar sua conta'}
          </h2>
          <p className="text-slate-500 text-sm mt-2">
            {type === 'register' ? 'Solicite seu desbloqueio em poucos minutos.' : 'Acompanhe o status da sua solicitação.'}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl flex items-center">
            <AlertCircle className="h-4 w-4 mr-2" /> {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {type === 'register' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nome Completo</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <input 
                  type="text" 
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                  placeholder="Seu nome"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Info className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input 
                type="email" 
                required
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                placeholder="seu@email.com"
                value={formData.email}
                onChange={e => setFormData({...formData, email: e.target.value})}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input 
                type="password" 
                required
                className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all"
                placeholder="••••••••"
                value={formData.password}
                onChange={e => setFormData({...formData, password: e.target.value})}
              />
            </div>
          </div>
          
          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-100 disabled:opacity-50"
          >
            {loading ? 'Processando...' : (type === 'register' ? 'Solicitar Desbloqueio' : 'Entrar')}
          </button>
        </form>

        <div className="mt-8 text-center text-sm text-slate-500">
          {type === 'register' ? (
            <p>Já tem uma conta? <button onClick={() => setPage('login')} className="text-emerald-600 font-bold">Entrar</button></p>
          ) : (
            <p>Não tem uma conta? <button onClick={() => setPage('register')} className="text-emerald-600 font-bold">Criar conta</button></p>
          )}
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100 text-center">
          <p className="text-xs text-slate-400">
            Seus dados não são armazenados e permanecem sigilosos.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

const Dashboard = ({ user, startFlow, refreshKey }: { user: UserData, startFlow: () => void, refreshKey: number }) => {
  const [requests, setRequests] = useState<UnlockRequest[]>([]);
  const [formData, setFormData] = useState({ contact_phone: '', unlock_number: '' });
  const [loading, setLoading] = useState(false);
  const [activeNotice, setActiveNotice] = useState<UnlockRequest | null>(null);

  const fetchRequests = async () => {
    const { data, error } = await supabase
      .from('unlock_requests')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });
    
    if (data) {
      setRequests(data);
      // Check for unread notices
      const unreadNotice = data.find(r => r.user_notice && !r.user_notice_read);
      if (unreadNotice) {
        setActiveNotice(unreadNotice);
      }
    }
    if (error) console.error('Error fetching requests:', error);
  };

  const dismissNotice = async () => {
    if (!activeNotice) return;
    
    try {
      await supabase
        .from('unlock_requests')
        .update({ user_notice_read: true })
        .eq('id', activeNotice.id);
      
      setActiveNotice(null);
      fetchRequests();
    } catch (err) {
      console.error('Error dismissing notice:', err);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [user.id, refreshKey]);

  const handleNewRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await supabase
        .from('unlock_requests')
        .insert([
          { 
            user_id: user.id, 
            name: user.name,
            email: user.email,
            contact_phone: formData.contact_phone,
            unlock_number: formData.unlock_number,
            status: 'Aguardando confirmação'
          }
        ]);
      
      if (!error) {
        setFormData({ contact_phone: '', unlock_number: '' });
        fetchRequests();
        startFlow();
      } else {
        console.error('Erro ao criar solicitação:', error.message);
      }
    } catch (err) {
      console.error('Erro inesperado:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Olá, {user.name}</h1>
            <p className="text-slate-500">Acompanhe suas solicitações de desbloqueio.</p>
          </div>
          <div className="bg-white px-4 py-2 rounded-xl border border-slate-200 flex items-center shadow-sm">
            <div className="h-2 w-2 rounded-full bg-emerald-500 mr-2 animate-pulse"></div>
            <span className="text-sm font-medium text-slate-600">Sistemas Operacionais</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* New Request Form */}
          <div className="lg:col-span-1">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Nova Solicitação</h2>
              <form onSubmit={handleNewRequest} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Seu Telefone de Contato</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input 
                      type="text" 
                      required
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 outline-none"
                      placeholder="+55 (00) 00000-0000"
                      value={formData.contact_phone}
                      onChange={e => setFormData({...formData, contact_phone: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Número a ser Desbloqueado</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                    <input 
                      type="text" 
                      required
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 outline-none"
                      placeholder="+55 (00) 00000-0000"
                      value={formData.unlock_number}
                      onChange={e => setFormData({...formData, unlock_number: e.target.value})}
                    />
                  </div>
                </div>
                <button 
                  type="submit"
                  disabled={loading || !formData.contact_phone.trim() || !formData.unlock_number.trim()}
                  className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Iniciando...' : 'Solicitar desbloqueio'}
                </button>
              </form>
              <div className="mt-6 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                <p className="text-xs text-emerald-700 leading-relaxed">
                  <Info className="h-3 w-3 inline mr-1" />
                  Após o início, nosso sistema levará até 6 horas para processar a solicitação junto aos servidores.
                </p>
              </div>
            </div>
          </div>

          {/* Requests List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                <h2 className="text-lg font-bold text-slate-900">Suas Solicitações</h2>
                <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{requests.length} total</span>
              </div>
              <div className="divide-y divide-slate-100">
                {requests.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="h-16 w-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Phone className="h-8 w-8 text-slate-300" />
                    </div>
                    <p className="text-slate-500">Nenhuma solicitação encontrada.</p>
                  </div>
                ) : (
                  requests.map((req) => (
                    <div key={req.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center">
                        <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center mr-4">
                          <Phone className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-bold text-slate-900">{req.unlock_number}</p>
                          <div className="flex flex-col gap-0.5">
                            <p className="text-xs text-slate-500 font-medium">Contato: {req.contact_phone}</p>
                            <p className="text-xs text-slate-400">Solicitado em {new Date(req.created_at).toLocaleDateString('pt-BR')}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-4">
                        <div className="flex flex-col items-end gap-2">
                          <div className={`px-3 py-1 rounded-full text-[10px] font-bold flex items-center ${
                            req.status === 'Concluído' ? 'bg-emerald-100 text-emerald-700' :
                            req.status === 'Falha técnica' ? 'bg-red-100 text-red-700' :
                            req.status === 'Em andamento' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {req.status === 'Aguardando confirmação' && <Clock className="h-3 w-3 mr-1" />}
                            {req.status === 'Em andamento' && <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 2 }} className="mr-1"><Zap className="h-3 w-3" /></motion.div>}
                            {req.status === 'Concluído' && <CheckCircle2 className="h-3 w-3 mr-1" />}
                            {req.status}
                          </div>
                          {req.payment_status && (
                            <span className={`text-[9px] font-bold px-2 py-0.5 rounded border ${
                              req.payment_status === 'Confirmado' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'
                            }`}>
                              Pagamento: {req.payment_status}
                            </span>
                          )}
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-300 hidden sm:block" />
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notice Popup */}
      <AnimatePresence>
        {activeNotice && (
          <div className="fixed inset-0 z-[150] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={dismissNotice}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="bg-amber-500 p-6 text-white text-center">
                <Bell className="h-12 w-12 mx-auto mb-2" />
                <h3 className="text-xl font-bold">Aviso Importante</h3>
              </div>
              <div className="p-8">
                <p className="text-slate-700 text-lg leading-relaxed mb-8 text-center">
                  {activeNotice.user_notice}
                </p>
                <button 
                  onClick={dismissNotice}
                  className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-black transition-all shadow-lg"
                >
                  Entendido
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const AdminLoginPage = ({ onAdminAuth, setPage }: { onAdminAuth: (u: UserData) => void, setPage: (p: Page) => void }) => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setError('O sistema de autenticação administrativa não está disponível.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (signInError) throw signInError;

      if (data.user) {
        // Check if user is in admin_users table
        const { data: adminData, error: adminError } = await supabase
          .from('admin_users')
          .select('id')
          .eq('id', data.user.id)
          .single();

        if (adminError || !adminData) {
          await supabase.auth.signOut();
          throw new Error('Acesso negado. Este usuário não tem privilégios administrativos.');
        }

        onAdminAuth({
          id: data.user.id,
          name: data.user.user_metadata.full_name || 'Administrador',
          email: data.user.email || credentials.email,
          isAdmin: true
        });
      }
    } catch (err: any) {
      setError(err.message || 'Erro na autenticação administrativa.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 flex items-center justify-center px-4 bg-slate-900">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8"
      >
        <div className="text-center mb-8">
          <div className="h-16 w-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lock className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Acesso Restrito</h2>
          <p className="text-slate-500 text-sm mt-2">Painel de Controle Administrativo</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl flex items-center">
            <AlertCircle className="h-4 w-4 mr-2" /> {error}
          </div>
        )}

        <form onSubmit={handleAdminLogin} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email ADM</label>
            <input 
              type="email" 
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 outline-none"
              placeholder="admin@unblocker.com"
              value={credentials.email}
              onChange={e => setCredentials({...credentials, email: e.target.value})}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Senha Mestra</label>
            <input 
              type="password" 
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 outline-none"
              placeholder="••••••••"
              value={credentials.password}
              onChange={e => setCredentials({...credentials, password: e.target.value})}
            />
          </div>
          <button 
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold hover:bg-black transition-all shadow-lg disabled:opacity-50"
          >
            {loading ? 'Autenticando...' : 'Acessar Painel'}
          </button>
          <button 
            type="button"
            onClick={() => setPage('home')}
            className="w-full text-slate-400 text-sm font-medium hover:text-slate-600 transition-colors"
          >
            Voltar para o site
          </button>
        </form>
      </motion.div>
    </div>
  );
};

const AdminDashboard = ({ user, logout }: { user: UserData, logout: () => void }) => {
  const [allRequests, setAllRequests] = useState<UnlockRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, { status?: string, payment_status?: string, admin_notes?: string, user_notice?: string, user_notice_read?: boolean }>>({});
  const [noticeModal, setNoticeModal] = useState<{ isOpen: boolean, requestId: string, message: string }>({
    isOpen: false,
    requestId: '',
    message: ''
  });
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean, requestId: string }>({
    isOpen: false,
    requestId: ''
  });
  const [globalAlertModal, setGlobalAlertModal] = useState({ isOpen: false, active: false, message: '' });
  const [diagnosticResults, setDiagnosticResults] = useState<Record<string, { success: boolean, message: string } | null>>({
    unlock_requests: null,
    admin_users: null,
    user_notices: null
  });
  const [isTesting, setIsTesting] = useState(false);

  const testDatabaseTables = async () => {
    setIsTesting(true);
    const tables = ['unlock_requests', 'admin_users', 'user_notices'];
    const results: any = {};

    for (const table of tables) {
      try {
        const { error } = await supabase.from(table).select('*').limit(1);
        if (error) {
          results[table] = { success: false, message: error.message };
        } else {
          results[table] = { success: true, message: 'Tabela acessível e funcional.' };
        }
      } catch (err: any) {
        results[table] = { success: false, message: err.message || 'Erro desconhecido' };
      }
    }

    setDiagnosticResults(results);
    setIsTesting(false);
  };

  const clearCompleted = async () => {
    if (confirm('Deseja remover todas as solicitações marcadas como "Concluído" ou "Finalizado"?')) {
      setLoading(true);
      try {
        const { error } = await supabase
          .from('unlock_requests')
          .delete()
          .in('status', ['Concluído', 'Finalizado', 'Sucesso']);
        
        if (!error) {
          fetchAllRequests();
          alert('Solicitações concluídas removidas com sucesso.');
        } else {
          alert('Erro ao limpar: ' + error.message);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const fetchAllRequests = async () => {
    const { data, error } = await supabase
      .from('unlock_requests')
      .select('*')
      .neq('id', '00000000-0000-0000-0000-000000000000') // Hide global alert record
      .order('created_at', { ascending: false });
    
    if (data) setAllRequests(data);
    if (error) console.error('Error fetching all requests:', error);
  };

  const fetchGlobalAlert = async () => {
    const { data, error } = await supabase
      .from('unlock_requests')
      .select('*')
      .eq('id', '00000000-0000-0000-0000-000000000000')
      .single();
    
    if (data) {
      setGlobalAlertModal({
        isOpen: false,
        active: data.status === 'active',
        message: data.user_notice || ''
      });
    }
  };

  useEffect(() => {
    fetchAllRequests();
    fetchGlobalAlert();
  }, []);

  const saveGlobalAlert = async () => {
    setLoading(true);
    try {
      // Try to update first
      const { error: updateError } = await supabase
        .from('unlock_requests')
        .update({
          status: globalAlertModal.active ? 'active' : 'inactive',
          user_notice: globalAlertModal.message,
          name: 'GLOBAL_ALERT',
          email: 'system@alert.com'
        })
        .eq('id', '00000000-0000-0000-0000-000000000000');

      if (updateError) {
        // If doesn't exist, insert
        await supabase.from('unlock_requests').insert([{
          id: '00000000-0000-0000-0000-000000000000',
          user_id: '00000000-0000-0000-0000-000000000000',
          name: 'GLOBAL_ALERT',
          email: 'system@alert.com',
          contact_phone: 'SYSTEM',
          unlock_number: 'SYSTEM',
          status: globalAlertModal.active ? 'active' : 'inactive',
          user_notice: globalAlertModal.message
        }]);
      }
      setGlobalAlertModal(prev => ({ ...prev, isOpen: false }));
      alert('Alerta Geral atualizado com sucesso!');
      window.location.reload(); // Refresh to apply to current session
    } catch (err) {
      console.error('Error saving global alert:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteRequest = async () => {
    const id = deleteModal.requestId;
    if (!id) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('unlock_requests')
        .delete()
        .eq('id', id);
      
      if (!error) {
        fetchAllRequests();
        setDeleteModal({ isOpen: false, requestId: '' });
      } else {
        alert('Erro ao excluir: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (requestId: string, field: string, value: any) => {
    setPendingChanges(prev => ({
      ...prev,
      [requestId]: {
        ...(prev[requestId] || {}),
        [field]: value
      }
    }));
  };

  const handleSetNotice = (requestId: string) => {
    const req = allRequests.find(r => r.id === requestId);
    setNoticeModal({
      isOpen: true,
      requestId,
      message: req?.user_notice || ''
    });
  };

  const saveNotice = () => {
    handleFieldChange(noticeModal.requestId, 'user_notice', noticeModal.message);
    handleFieldChange(noticeModal.requestId, 'user_notice_read', false);
    setNoticeModal(prev => ({ ...prev, isOpen: false }));
  };

  const saveChanges = async (requestId: string) => {
    const changes = pendingChanges[requestId];
    if (!changes) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('unlock_requests')
        .update(changes)
        .eq('id', requestId);
      
      if (!error) {
        setPendingChanges(prev => {
          const next = { ...prev };
          delete next[requestId];
          return next;
        });
        fetchAllRequests();
      } else {
        alert('Erro ao salvar alterações: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Painel Administrativo</h1>
            <p className="text-slate-500">Gerencie todas as solicitações do sistema.</p>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={clearCompleted}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-all"
            >
              <Trash2 className="h-4 w-4" />
              Limpar Concluídos
            </button>
            <button 
              onClick={testDatabaseTables}
              disabled={isTesting}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
            >
              <Activity className={`h-4 w-4 ${isTesting ? 'animate-spin' : ''}`} />
              {isTesting ? 'Testando...' : 'Testar Tabelas'}
            </button>
            <button 
              onClick={() => setGlobalAlertModal(prev => ({ ...prev, isOpen: true }))}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                globalAlertModal.active 
                  ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              <Megaphone className={`h-4 w-4 ${globalAlertModal.active ? 'animate-bounce' : ''}`} />
              Alerta Geral: {globalAlertModal.active ? 'ATIVO' : 'DESATIVADO'}
            </button>
            <div className="bg-emerald-600 text-white px-4 py-2 rounded-xl text-sm font-bold">
              Modo Administrador
            </div>
            <button 
              onClick={logout}
              className="p-2 text-slate-400 hover:text-red-600 transition-colors"
              title="Sair do Painel"
            >
              <LogOut className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Diagnostic Results */}
        {(diagnosticResults.unlock_requests || diagnosticResults.admin_users || diagnosticResults.user_notices) && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {(Object.entries(diagnosticResults) as [string, { success: boolean, message: string } | null][]).map(([table, result]) => result && (
              <motion.div 
                key={table}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`p-4 rounded-2xl border ${
                  result.success 
                    ? 'bg-emerald-50 border-emerald-100 text-emerald-800' 
                    : 'bg-red-50 border-red-100 text-red-800'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4 opacity-50" />
                    <span className="font-bold text-sm uppercase tracking-wider">{table}</span>
                  </div>
                  {result.success ? <CheckCircle2 className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
                </div>
                <p className="text-[10px] leading-tight opacity-80">{result.message}</p>
              </motion.div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
            <h2 className="text-lg font-bold text-slate-900">Todas as Solicitações</h2>
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">{allRequests.length} total</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <th className="px-6 py-4 font-bold">Usuário</th>
                  <th className="px-6 py-4 font-bold">Número Desbloqueio</th>
                  <th className="px-6 py-4 font-bold">Contato</th>
                  <th className="px-6 py-4 font-bold">Pagamento</th>
                  <th className="px-6 py-4 font-bold">Status</th>
                  <th className="px-6 py-4 font-bold">Notas ADM</th>
                  <th className="px-6 py-4 font-bold">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {allRequests.map((req) => (
                  <tr key={req.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-bold text-slate-900 text-sm">{req.name}</p>
                      <p className="text-slate-400 text-[10px]">{req.email}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600 font-mono text-sm">{req.unlock_number}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-slate-600 text-sm">{req.contact_phone}</p>
                      <p className="text-slate-400 text-[10px]">{new Date(req.created_at).toLocaleString('pt-BR')}</p>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={pendingChanges[req.id]?.payment_status || req.payment_status || 'Pendente'}
                        onChange={(e) => handleFieldChange(req.id, 'payment_status', e.target.value)}
                        className={`text-[10px] font-bold border rounded-lg p-1 outline-none ${
                          (pendingChanges[req.id]?.payment_status || req.payment_status) === 'Confirmado' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}
                      >
                        <option value="Pendente">Pendente</option>
                        <option value="Confirmado">Confirmado</option>
                        <option value="Estornado">Estornado</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <select 
                        value={pendingChanges[req.id]?.status || req.status}
                        onChange={(e) => handleFieldChange(req.id, 'status', e.target.value)}
                        className="text-[10px] border border-slate-200 rounded-lg p-1 outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                      >
                        <option value="Aguardando confirmação">Aguardando confirmação</option>
                        <option value="Em andamento">Em andamento</option>
                        <option value="Concluído">Concluído</option>
                        <option value="Falha técnica">Falha técnica</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <textarea 
                        value={pendingChanges[req.id]?.admin_notes !== undefined ? pendingChanges[req.id]?.admin_notes : (req.admin_notes || '')}
                        onChange={(e) => handleFieldChange(req.id, 'admin_notes', e.target.value)}
                        placeholder="Notas..."
                        className="text-[10px] border border-slate-200 rounded-lg p-1 w-32 h-10 outline-none focus:ring-2 focus:ring-emerald-500 bg-white resize-none"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {pendingChanges[req.id] && (
                          <button 
                            onClick={() => saveChanges(req.id)}
                            disabled={loading}
                            className="text-[10px] bg-emerald-600 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-emerald-700 transition-all shadow-md shadow-emerald-100 disabled:opacity-50 flex items-center"
                          >
                            <Check className="h-3 w-3 mr-1" /> Salvar
                          </button>
                        )}
                        <button 
                          onClick={() => handleSetNotice(req.id)}
                          className={`p-2 rounded-lg transition-all ${
                            req.user_notice ? 'bg-amber-100 text-amber-600 hover:bg-amber-200' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                          }`}
                          title="Enviar Aviso"
                        >
                          <Bell className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => setDeleteModal({ isOpen: true, requestId: req.id })}
                          className="p-2 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 hover:text-red-600 transition-all"
                          title="Excluir Solicitação"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {deleteModal.isOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setDeleteModal({ isOpen: false, requestId: '' })}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="bg-red-600 p-6 text-white text-center">
                <Trash2 className="h-12 w-12 mx-auto mb-2" />
                <h3 className="text-xl font-bold">Confirmar Exclusão</h3>
              </div>
              <div className="p-8 text-center">
                <p className="text-slate-600 mb-8">Tem certeza que deseja excluir esta solicitação permanentemente? Esta ação não pode ser desfeita.</p>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setDeleteModal({ isOpen: false, requestId: '' })}
                    className="flex-1 px-4 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition-all"
                  >
                    Não
                  </button>
                  <button 
                    onClick={deleteRequest}
                    disabled={loading}
                    className="flex-1 bg-red-600 text-white py-3 rounded-xl font-bold hover:bg-red-700 transition-all shadow-lg disabled:opacity-50"
                  >
                    {loading ? 'Excluindo...' : 'Sim, Excluir'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {noticeModal.isOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setNoticeModal(prev => ({ ...prev, isOpen: false }))}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="bg-emerald-600 p-6 text-white text-center">
                <Bell className="h-12 w-12 mx-auto mb-2" />
                <h3 className="text-xl font-bold">Enviar Aviso ao Cliente</h3>
              </div>
              <div className="p-8">
                <label className="block text-sm font-medium text-slate-700 mb-2">Mensagem de Alerta</label>
                <textarea 
                  value={noticeModal.message}
                  onChange={(e) => setNoticeModal(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Digite a mensagem que o cliente verá ao logar..."
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 outline-none h-32 resize-none mb-6"
                />
                <div className="flex gap-3">
                  <button 
                    onClick={() => setNoticeModal(prev => ({ ...prev, isOpen: false }))}
                    className="flex-1 px-4 py-3 rounded-xl font-bold text-slate-500 hover:bg-slate-100 transition-all"
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={saveNotice}
                    className="flex-1 bg-emerald-600 text-white py-3 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg"
                  >
                    Confirmar
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

const StaticPage = ({ title, content }: { title: string, content: React.ReactNode }) => (
  <div className="pt-24 pb-20 bg-white">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-slate-900 mb-8">{title}</h1>
      <div className="prose prose-slate max-w-none text-slate-600 leading-relaxed">
        {content}
      </div>
    </div>
  </div>
);

const ConfigPage = () => {
  const [url, setUrl] = useState('');
  const [key, setKey] = useState('');

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (url && key) {
      updateSupabaseConfig(url, key);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full bg-white rounded-3xl shadow-2xl p-8"
      >
        <div className="text-center mb-8">
          <div className="h-16 w-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Settings className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Configuração Necessária</h2>
          <p className="text-slate-500 text-sm mt-2">Insira suas chaves do Supabase para ativar o sistema.</p>
        </div>

        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Supabase URL</label>
            <input 
              type="url" 
              required
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 outline-none"
              placeholder="https://xyz.supabase.co"
              value={url}
              onChange={e => setUrl(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Supabase Anon Key</label>
            <textarea 
              required
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
              placeholder="eyJhbGci..."
              value={key}
              onChange={e => setKey(e.target.value)}
            />
          </div>
          <button 
            type="submit"
            className="w-full bg-emerald-600 text-white py-4 rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg"
          >
            Salvar e Ativar Sistema
          </button>
        </form>
        
        <div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-100">
          <p className="text-xs text-amber-700 leading-relaxed">
            <Info className="h-3 w-3 inline mr-1" />
            Essas chaves são necessárias para que o login e o banco de dados funcionem. Você as encontra no painel do Supabase em Settings &gt; API.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

// --- Main App ---

export default function App() {
  const [page, setPage] = useState<Page>('home');
  const [user, setUser] = useState<UserData | null>(null);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(() => sessionStorage.getItem('admin_logged_in') === 'true');
  const [supabaseStatus, setSupabaseStatus] = useState<'checking' | 'connected' | 'error'>('checking');
  const [supabaseErrorMessage, setSupabaseErrorMessage] = useState<string | null>(null);
  const [guestFlowStep, setGuestFlowStep] = useState<number>(0);
  const [copied, setCopied] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<{ contact_phone: string, unlock_number: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [globalAlert, setGlobalAlert] = useState<{ active: boolean, message: string } | null>(null);

  const paymentKey = "00020126360014br.gov.bcb.pix0114+5531971327426520400005303986540520.005802BR5919Leci Alves Dos Reis6009Sao Paulo62240520daqr2069366134295160630492DD";

  const fetchGlobalAlert = async () => {
    if (!supabase) return;
    try {
      // We use a special ID for global alert in unlock_requests table to avoid creating new tables
      const { data, error } = await supabase
        .from('unlock_requests')
        .select('*')
        .eq('id', '00000000-0000-0000-0000-000000000000')
        .single();
      
      if (data) {
        setGlobalAlert({
          active: data.status === 'active',
          message: data.user_notice || ''
        });
      }
    } catch (err) {
      console.error('Error fetching global alert:', err);
    }
  };

  useEffect(() => {
    fetchGlobalAlert();
  }, []);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(paymentKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const startFlow = () => {
    setGuestFlowStep(1);
  };

  const handleConfirmPayment = async () => {
    setGuestFlowStep(3);
  };

  useEffect(() => {
    const checkConnection = async () => {
      try {
        if (!supabase) {
          console.warn('Supabase client not initialized.');
          setSupabaseStatus('error');
          setSupabaseErrorMessage('Cliente Supabase não inicializado.');
          return;
        }

        const { error } = await supabase.from('unlock_requests').select('count', { count: 'exact', head: true });
        if (error && error.code !== 'PGRST116' && error.code !== '42P01' && error.code !== '42501') {
          console.error('Supabase connection error:', error);
          setSupabaseStatus('error');
          setSupabaseErrorMessage(`Erro do Supabase: ${error.message}`);
        } else {
          setSupabaseStatus('connected');
          setSupabaseErrorMessage(null);
        }
      } catch (err: any) {
        console.error('Supabase connection failed:', err);
        setSupabaseStatus('error');
        setSupabaseErrorMessage(`Falha na conexão: ${err.message || 'Erro desconhecido'}`);
      }
    };

    checkConnection();

    // Listen for auth changes
    if (!supabase) return;
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth event:', event);
      if (session?.user) {
        try {
          // Check if admin
          const { data: adminData, error: adminError } = await supabase
            .from('admin_users')
            .select('id')
            .eq('id', session.user.id)
            .single();

          if (adminError && adminError.code !== 'PGRST116') {
            console.error('Error checking admin status:', adminError);
          }

          const userData: UserData = {
            id: session.user.id,
            name: session.user.user_metadata.full_name || (adminData ? 'Administrador' : 'Usuário'),
            email: session.user.email || '',
            isAdmin: !!adminData
          };
          
          setUser(userData);
          if (adminData) {
            setIsAdminLoggedIn(true);
            sessionStorage.setItem('admin_logged_in', 'true');
          } else {
            // If not admin but was previously marked as admin in session, clear it
            if (sessionStorage.getItem('admin_logged_in') === 'true') {
              setIsAdminLoggedIn(false);
              sessionStorage.removeItem('admin_logged_in');
            }
          }
        } catch (err) {
          console.error('Unexpected error in auth change listener:', err);
        }
      } else {
        setUser(null);
        setIsAdminLoggedIn(false);
        sessionStorage.removeItem('admin_logged_in');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleAuth = async (userData: UserData) => {
    setUser(userData);
    setPage('dashboard');
  };

  const handleAdminAuth = (adminData: UserData) => {
    setIsAdminLoggedIn(true);
    setUser(adminData);
    setPage('admin_dashboard');
  };

  const logout = async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    setUser(null);
    setIsAdminLoggedIn(false);
    sessionStorage.removeItem('admin_logged_in');
    setPage('home');
  };

  const renderPage = () => {
    switch (page) {
      case 'home': return <HomePage setPage={setPage} />;
      case 'login': return <AuthPage type="login" setPage={setPage} onAuth={handleAuth} />;
      case 'register': return <AuthPage type="register" setPage={setPage} onAuth={handleAuth} />;
      case 'admin_login': return <AdminLoginPage onAdminAuth={handleAdminAuth} setPage={setPage} />;
      case 'admin_dashboard': return isAdminLoggedIn && user ? <AdminDashboard user={user} logout={logout} /> : <AdminLoginPage onAdminAuth={handleAdminAuth} setPage={setPage} />;
      case 'dashboard': return user ? <Dashboard user={user} startFlow={startFlow} refreshKey={refreshKey} /> : <AuthPage type="login" setPage={setPage} onAuth={handleAuth} />;
      case 'about': return (
        <StaticPage 
          title="Sobre o UnBlocker WA 24hs" 
          content={
            <div className="space-y-4">
              <p>O UnBlocker WA 24hs nasceu com o objetivo de devolver aos usuários o acesso ao WhatsApp de forma rápida e segura. Nossa equipe utiliza métodos avançados e atualizados constantemente para garantir eficiência no processo, sempre respeitando a privacidade do usuário.</p>
              <p>Entendemos que hoje o WhatsApp é mais do que um aplicativo de mensagens; é uma ferramenta de trabalho, conexão e vida pessoal. Um bloqueio injusto ou inesperado pode causar prejuízos reais.</p>
              <p>Nossa missão é democratizar o acesso ao suporte técnico especializado, oferecendo uma alternativa ágil aos canais oficiais que muitas vezes demoram dias para responder.</p>
            </div>
          } 
        />
      );
      case 'privacy': return (
        <StaticPage 
          title="Política de Privacidade" 
          content={
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-slate-900">Privacidade em Primeiro Lugar</h3>
              <p>No UnBlocker WA 24hs, levamos sua privacidade a sério. Aqui estão nossos compromissos fundamentais:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li><strong>Não armazenamos dados pessoais:</strong> Suas informações são utilizadas exclusivamente para o processo de solicitação de desbloqueio.</li>
                <li><strong>Não compartilhamos informações:</strong> Seus dados nunca serão vendidos ou compartilhados com terceiros.</li>
                <li><strong>Exclusão automática:</strong> Após a conclusão do processo, dados sensíveis são removidos de nossos sistemas ativos.</li>
                <li><strong>Segurança:</strong> Utilizamos criptografia de ponta a ponta em todas as comunicações.</li>
              </ul>
            </div>
          } 
        />
      );
      case 'faq': return (
        <StaticPage 
          title="Perguntas Frequentes (FAQ)" 
          content={
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Quanto tempo demora o processo?</h3>
                <p>O tempo médio para o desbloqueio ser processado é de até 6 horas. Em alguns casos complexos, pode levar um pouco mais, mas sempre mantemos você informado pelo painel.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Preciso pagar antes?</h3>
                <p>Sim, pois nosso trabalho depende muito da complexidade, podem ser oferecidos serviços premium para aceleração, mas você sempre será informado antecipadamente.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Meu número pode não voltar?</h3>
                <p>Nossa taxa de sucesso é de 92%. Embora não possamos garantir 100% devido às políticas internas do WhatsApp, nossos métodos são os mais eficazes do mercado.</p>
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Meus dados ficam salvos?</h3>
                <p>Não. Mantemos apenas o registro da sua conta e o status da solicitação para que você possa acompanhar. Dados de conversas ou contatos nunca são acessados por nós.</p>
              </div>
            </div>
          } 
        />
      );
      default: return <HomePage setPage={setPage} />;
    }
  };

  if (!supabase) {
    return <ConfigPage />;
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Navbar currentPage={page} setPage={setPage} user={user} logout={logout} />
      
      {/* Global Alert Popup (Before Login) */}
      <AnimatePresence>
        {globalAlert?.active && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[40px] shadow-2xl overflow-hidden border-4 border-emerald-500"
            >
              <div className="bg-emerald-600 p-8 text-white text-center relative">
                <div className="absolute top-4 right-4">
                  <button 
                    onClick={() => setGlobalAlert(prev => prev ? { ...prev, active: false } : null)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <div className="h-20 w-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 backdrop-blur-sm">
                  <Megaphone className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-2xl font-black uppercase tracking-tighter">Comunicado Importante</h3>
              </div>
              <div className="p-10 text-center">
                <p className="text-slate-700 text-xl font-medium leading-relaxed mb-10">
                  {globalAlert.message}
                </p>
                <button 
                  onClick={() => setGlobalAlert(prev => prev ? { ...prev, active: false } : null)}
                  className="w-full bg-emerald-600 text-white py-5 rounded-2xl font-black text-lg hover:bg-emerald-700 transition-all shadow-xl shadow-emerald-200 uppercase tracking-widest"
                >
                  Entendido e Prosseguir
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {supabaseStatus === 'error' && (
        <div className="fixed top-20 left-0 right-0 z-[60] bg-red-600 text-white text-center py-2 px-4 text-sm font-medium">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2">
            <span>Erro de conexão com o banco de dados.</span>
            {supabaseErrorMessage && <span className="opacity-90 text-xs bg-black/20 px-2 py-0.5 rounded font-mono">{supabaseErrorMessage}</span>}
          </div>
        </div>
      )}
      
      <main className="flex-grow pt-20">
        {renderPage()}
      </main>
      <Footer setPage={setPage} />

      {/* Guest Flow Popups */}
      <AnimatePresence>
        {guestFlowStep === 1 && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setGuestFlowStep(0)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="bg-[#075E54] p-6 text-white text-center">
                <h3 className="text-xl font-bold flex items-center justify-center gap-2">
                  <span>🔓</span> Solicitação de Desbloqueio
                </h3>
              </div>
              
              <div className="p-8 space-y-6">
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  <blockquote className="border-l-4 border-[#25D366] pl-4 italic bg-slate-50 py-2">
                    O tempo estimado para desbloqueio é de aproximadamente 6 horas e 20 minutos, podendo ocorrer antes ou a qualquer momento dentro deste período.
                  </blockquote>
                  <p>
                    Para a realização do desbloqueio será cobrado o valor de <span className="font-bold text-slate-900">R$ 20,00</span>.
                  </p>
                  <p>
                    Caso o desbloqueio não ocorra dentro do prazo informado, realizaremos o estorno integral do valor pago para a mesma chave PIX utilizada no pagamento.
                  </p>
                  <p className="font-medium text-slate-900">
                    Se estiver de acordo, clique em Prosseguir para continuar.
                  </p>
                </div>
                
                <div className="flex gap-4">
                  <button 
                    onClick={() => setGuestFlowStep(0)}
                    className="flex-1 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
                  >
                    <span>❌</span> Cancelar
                  </button>
                  <button 
                    onClick={() => setGuestFlowStep(2)}
                    className="flex-1 bg-[#25D366] text-white py-3 rounded-xl font-bold hover:bg-[#128C7E] transition-all shadow-lg shadow-[#25D366]/20 flex items-center justify-center gap-2"
                  >
                    <span>✅</span> Prosseguir
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {guestFlowStep === 2 && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setGuestFlowStep(0)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="bg-[#075E54] p-6 text-white text-center">
                <h3 className="text-xl font-bold flex items-center justify-center gap-2">
                  <span>💰</span> Pagamento via PIX
                </h3>
              </div>
              
              <div className="p-8 flex flex-col items-center">
                <div className="bg-slate-50 p-4 rounded-2xl border-2 border-slate-100 mb-6">
                  <img 
                    src="https://vyemarhrtuzplljpfpbs.supabase.co/storage/v1/object/public/Imagens/bg%20qr.png" 
                    alt="QR Code de Pagamento" 
                    className="w-48 h-48 object-contain"
                  />
                </div>
                
                <div className="w-full mb-6">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-xs font-mono text-slate-600 break-all mb-3 text-center">
                    {paymentKey}
                  </div>
                  <button 
                    onClick={copyToClipboard}
                    className="w-full bg-[#25D366] text-white py-3 rounded-xl font-bold hover:bg-[#128C7E] transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                    {copied ? 'Copiado!' : 'Copiar chave PIX'}
                  </button>
                </div>
                
                <div className="space-y-6 text-center w-full">
                  <blockquote className="text-sm text-slate-600 leading-relaxed italic bg-amber-50 p-4 rounded-xl border border-amber-100">
                    Após realizar o pagamento, clique no botão Confirmar Pagamento abaixo para darmos continuidade ao processo.
                  </blockquote>
                  
                  <button 
                    onClick={handleConfirmPayment}
                    className="w-full bg-[#075E54] text-white py-4 rounded-xl font-bold hover:bg-black transition-all shadow-lg flex items-center justify-center gap-2"
                  >
                    <span>✅</span> Confirmar Pagamento
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {guestFlowStep === 3 && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setGuestFlowStep(0)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="bg-[#25D366] p-8 text-white text-center">
                <div className="h-20 w-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-12 w-12 text-white" />
                </div>
                <h3 className="text-2xl font-bold flex items-center justify-center gap-2">
                  <span>✅</span> Pagamento em Análise
                </h3>
              </div>
              
              <div className="p-8 space-y-6">
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  <p className="text-center font-bold text-slate-900 text-lg">
                    Agradecemos pelo pagamento.
                  </p>
                  <p>
                    Assim que o valor for constatado em nosso sistema, daremos início ao processo de desbloqueio.
                  </p>
                  <div className="p-5 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-sm text-center">
                      Para acompanhar o status basta realizar login e acompanhar em tempo real
                    </p>
                  </div>
                </div>
                
                <button 
                  onClick={() => {
                    setGuestFlowStep(0);
                    if (!user) setPage('login');
                  }}
                  className="w-full bg-[#075E54] text-white py-4 rounded-xl font-bold hover:bg-black transition-all shadow-lg flex items-center justify-center gap-2"
                >
                  <span>{user ? '✅' : '🔐'}</span> {user ? 'Entendido' : 'Ir para Login'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
